import type {
  AuthenticatedUserContract,
  Locale,
  StudentReplicaContract,
} from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { Badge } from "@chams-pay/design-system/components/badge";
import { Button } from "@chams-pay/design-system/components/button";
import { Input } from "@chams-pay/design-system/components/input";
import { Label } from "@chams-pay/design-system/components/label";
import type { PasskeyUnlockMaterial } from "@chams-pay/local-store";
import { Block, Navbar, Page, Segmented, SegmentedButton } from "konsta/react";
import { Eye, EyeOff, Languages } from "lucide-react";
import type { FormEvent } from "react";
import { useState } from "react";
import {
  deviceFingerprintLabel,
  type AccessAuthenticatedHandler,
  useAccess,
  useExistingReplica,
  useInstallation,
  useOfflineSetup,
  useUnlock,
} from "../core/use-session.js";
import {
  Keypad,
  LockedTerminalCard,
  PinDots,
  looksPredictable,
  useLockedScreen,
} from "../desktop/gates.js";
import { Wordmark } from "../ui/brand.js";

type TranslationKey = Parameters<typeof translate>[1];

const PIN_LENGTH = 8;

function stop(event: FormEvent, run: () => void) {
  event.preventDefault();
  run();
}

function LanguageButton({
  locale,
  onChange,
  tone = "light",
}: {
  readonly locale: Locale;
  readonly onChange: () => void;
  readonly tone?: "light" | "dark";
}) {
  const label = locale === "ar" ? "Français" : "العربية";
  return (
    <button
      type="button"
      onClick={onChange}
      aria-label={label}
      className={`floating-language-switch me-1 inline-flex h-11 min-h-11 items-center gap-1.5 rounded-full px-3 font-heading text-sm font-bold ${
        tone === "dark" ? "text-white" : "text-primary-strong"
      }`}
    >
      <Languages aria-hidden="true" className="size-4" />
      {label}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Sign in                                                             */
/* ------------------------------------------------------------------ */

export function MobileAccessScreen({
  locale,
  onLocaleChange,
  onAuthenticated,
}: {
  readonly locale: Locale;
  readonly onLocaleChange: () => void;
  readonly onAuthenticated: AccessAuthenticatedHandler;
}) {
  const t = (key: TranslationKey) => translate(locale, key);
  const access = useAccess(onAuthenticated);
  const hasReplica = useExistingReplica();
  const phase = access.phase;

  const heading =
    access.mode === "enroll"
      ? phase === "submitting"
        ? t("entry.enrollTitleSubmit")
        : phase === "error"
          ? t("entry.enrollTitleErr")
          : t("entry.enrollTitleIdle")
      : phase === "submitting"
        ? t("entry.signinTitleSubmit")
        : phase === "error"
          ? t("entry.signinTitleErr")
          : t("entry.signinTitleIdle");
  const subheading =
    access.mode === "enroll"
      ? phase === "submitting"
        ? t("entry.enrollSubSubmit")
        : phase === "error"
          ? t("entry.enrollSubErr")
          : t("entry.enrollSubIdle")
      : phase === "submitting"
        ? t("entry.signinSubSubmit")
        : phase === "error"
          ? t("entry.signinSubErr")
          : t("entry.signinSubIdle");
  const primaryLabel =
    access.mode === "enroll"
      ? phase === "submitting"
        ? t("entry.enrollBtnSubmit")
        : phase === "error"
          ? t(
              access.registrationFallbackReady
                ? "entry.enrollRetryCompatible"
                : "entry.enrollRetry",
            )
          : t("entry.registerPasskey")
      : phase === "submitting"
        ? t("entry.btnSubmit")
        : phase === "error"
          ? t("entry.btnErr")
          : t("entry.btnIdle");
  const errorTitle = t(access.mode === "enroll" ? "entry.enrollErrTitle" : "entry.signinErrTitle");
  const errorBody = t(
    access.mode === "enroll"
      ? access.registrationFallbackReady
        ? "entry.enrollErrCompatibleBody"
        : "entry.enrollErrBody"
      : "entry.signinErrBody",
  );

  return (
    <Page className="chams-page access-page bg-surface-2" data-testid="login-screen">
      <Navbar
        className="sticky top-0 z-30"
        title={<Wordmark />}
        left={<span aria-hidden="true" className="chams-logo ms-3 size-7 bg-primary" />}
        right={<LanguageButton locale={locale} onChange={onLocaleChange} />}
      />

      <Block className="!mx-4 !mt-4 flex flex-col gap-3 rounded-2xl bg-primary-strong !p-5">
        <h1 className="font-heading text-xl font-bold leading-[1.4] text-white">
          {t("entry.heroTitle")}
        </h1>
        <p className="text-[12.5px] leading-[1.8] text-white/70">{t("entry.heroBody")}</p>
        <p className="flex w-fit items-center gap-2.5 rounded-lg bg-white/10 px-3 py-2 text-[11px] text-white/85">
          <span aria-hidden="true" className="size-[7px] rounded-full bg-[#7de2a8]" />
          {t("entry.heroStatus")}
        </p>
      </Block>

      <Block className="!my-5 flex animate-fade-up flex-col gap-[7px]" aria-live="polite">
        <h2 className="font-heading text-xl font-bold leading-[1.3]">{heading}</h2>
        <p className="text-[12.5px] leading-[1.6] text-muted-foreground">{subheading}</p>
      </Block>

      <Block className="!my-4">
        <Segmented
          strong
          role="tablist"
          aria-label={t("entry.tabLogin")}
          className="access-tabs mobile-segmented"
          data-tab-count="2"
          data-active-index={access.mode === "login" ? "0" : "1"}
        >
          <SegmentedButton
            role="tab"
            aria-selected={access.mode === "login"}
            active={access.mode === "login"}
            disabled={access.busy}
            onClick={() => access.setMode("login")}
            className="min-h-11 px-3 text-[12.5px] font-bold whitespace-nowrap"
          >
            {t("entry.tabLogin")}
          </SegmentedButton>
          <SegmentedButton
            role="tab"
            aria-selected={access.mode === "enroll"}
            active={access.mode === "enroll"}
            disabled={access.busy}
            onClick={() => access.setMode("enroll")}
            className="min-h-11 px-3 text-[12.5px] font-bold whitespace-nowrap"
          >
            {t("entry.tabRegister")}
          </SegmentedButton>
        </Segmented>
      </Block>

      <form
        className="access-form"
        onSubmit={(event: FormEvent) => stop(event, () => void access.submit())}
      >
        <Block className="flex animate-fade-up flex-col gap-4">
          {access.mode === "login" ? (
            <>
              <Label className="flex flex-col items-stretch gap-2 text-start">
                <span className="font-heading text-[11.5px] font-bold text-muted-foreground">
                  {t("entry.username")}
                </span>
                <Input
                  className="h-[46px] rounded-[9px] px-[13px] text-[13.5px]"
                  dir="ltr"
                  value={access.identifier}
                  onChange={(event) => access.setIdentifier(event.target.value)}
                  autoComplete="username webauthn"
                  required
                />
              </Label>
              <div className="flex items-center gap-3 rounded-[11px] border border-teal-100 bg-teal-50 p-4">
                <span className="grid size-11 shrink-0 place-items-center rounded-full bg-card motion-safe:animate-pulse-ring">
                  <span aria-hidden="true" className="chams-logo size-5 bg-primary" />
                </span>
                <span className="flex flex-col gap-[3px]">
                  <span className="font-heading text-[13px] font-bold leading-[1.35] text-primary-strong">
                    {t("entry.passkeyTitle")}
                  </span>
                  <span className="text-[11.5px] leading-[1.6] text-teal-600">
                    {t("entry.passkeyBody")}
                  </span>
                </span>
              </div>
            </>
          ) : (
            <Label className="flex flex-col items-stretch gap-2 text-start">
              <span className="font-heading text-[11.5px] font-bold text-muted-foreground">
                {t("entry.oneTimeCode")}
              </span>
              <Input
                className="chams-figure h-14 rounded-[9px] px-4 text-center text-[18px] font-bold tracking-[0.08em]"
                dir="ltr"
                value={access.code}
                onChange={(event) => access.setCode(event.target.value)}
                autoComplete="one-time-code"
                spellCheck={false}
                required
              />
              <small className="text-[11.5px] font-normal leading-[1.6] text-muted-2">
                {t("entry.codeHint")}
              </small>
            </Label>
          )}

          {phase === "error" && (
            <div
              role="alert"
              data-testid="access-error"
              className="flex animate-shake flex-col gap-1 rounded-[11px] border border-destructive-border bg-destructive-soft px-4 py-3.5 text-destructive"
            >
              <span className="font-heading text-[12.5px] font-bold leading-[1.4]">
                {errorTitle}
              </span>
              <span className="text-[11.5px] leading-[1.6]">{errorBody}</span>
            </div>
          )}

          {access.notice === "enrolled" && (
            <div
              role="status"
              className="flex animate-fade-up flex-col gap-1 rounded-[11px] border border-success-border bg-success-soft px-4 py-3.5 text-success"
            >
              <span className="font-heading text-[13px] font-bold leading-[1.35]">
                {t("entry.regDoneTitle")}
              </span>
              <span className="text-[11.5px] leading-[1.55]">{t("entry.regDoneBody")}</span>
            </div>
          )}
          {access.notice === "existing-passkey" && (
            <div
              role="status"
              className="flex animate-fade-up flex-col gap-1 rounded-[11px] border border-teal-100 bg-teal-50 px-4 py-3.5 text-primary-strong"
            >
              <span className="font-heading text-[13px] font-bold leading-[1.35]">
                {t("entry.existingPasskeyTitle")}
              </span>
              <span className="text-[11.5px] leading-[1.55]">{t("entry.existingPasskeyBody")}</span>
            </div>
          )}
        </Block>

        <Block className="!mb-8 flex flex-col gap-3">
          <Button
            type="submit"
            size="lg"
            disabled={access.busy}
            className={`access-submit w-full rounded-[10px] ${
              phase === "submitting" ? "bg-primary-hover disabled:bg-primary-hover" : ""
            }`}
          >
            {primaryLabel}
          </Button>
          {access.mode === "login" && hasReplica && (
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex min-h-11 items-center justify-center rounded-md font-heading text-xs font-bold text-primary outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {t("entry.useOffline")}
            </button>
          )}
        </Block>
      </form>
    </Page>
  );
}

/* ------------------------------------------------------------------ */
/* Device authorization                                                */
/* ------------------------------------------------------------------ */

export function MobileInstallationScreen({
  locale,
  user,
  csrf,
  onLocaleChange,
  onReady,
  onLogout,
}: {
  readonly locale: Locale;
  readonly user: AuthenticatedUserContract;
  readonly csrf: string;
  readonly onLocaleChange: () => void;
  readonly onReady: (csrf: string) => void;
  readonly onLogout: () => void;
}) {
  const t = (key: TranslationKey) => translate(locale, key);
  const gate = useInstallation(user, csrf, onReady);
  const fingerprint = deviceFingerprintLabel(gate.installation);
  const state = gate.failed
    ? "error"
    : !gate.installation
      ? "new"
      : gate.canAuthorize
        ? "review"
        : "waiting";

  const title =
    state === "new"
      ? t("entry.dev1Title")
      : state === "waiting"
        ? t("entry.dev2Title")
        : state === "review"
          ? t("entry.dev3Title")
          : t("entry.dev4Title");
  const body =
    state === "new"
      ? t("entry.dev1Body")
      : state === "waiting"
        ? t("entry.dev2Body")
        : state === "review"
          ? t("entry.dev3Body")
          : t("entry.dev4Body");

  return (
    <Page className="chams-page bg-canvas" data-testid="installation-gate">
      <Navbar
        className="sticky top-0 z-30"
        title={t("entry.deviceFingerprint")}
        right={<LanguageButton locale={locale} onChange={onLocaleChange} />}
      />

      <Block
        className={`flex animate-fade-up flex-col gap-4 ${state === "error" ? "animate-shake" : ""}`}
      >
        {state === "waiting" ? (
          <span className="grid size-11 place-items-center rounded-lg bg-warning-soft motion-safe:animate-pulse-ring">
            <span className="block size-[19px] rounded-full border-[2.4px] border-warning-border border-t-warning motion-safe:animate-spin" />
          </span>
        ) : state === "error" ? (
          <span className="grid size-11 place-items-center rounded-lg bg-[#fdf0ef] font-heading text-[21px] font-extrabold text-destructive">
            !
          </span>
        ) : (
          <span
            className={`grid size-11 place-items-center rounded-lg ${
              state === "review" ? "bg-teal-50" : "bg-sunken"
            }`}
          >
            <span
              aria-hidden="true"
              className={`chams-logo size-5 ${state === "review" ? "bg-primary" : "bg-muted-foreground"}`}
            />
          </span>
        )}
        <h1 className="font-heading text-base font-bold leading-[1.4]">{title}</h1>
        <p className="text-[12.5px] leading-[1.8] text-muted-foreground">{body}</p>

        <div className="flex flex-col gap-[5px] rounded-[9px] border border-sunken bg-surface-2 px-[14px] py-3">
          <span className="chams-eyebrow">{t("entry.deviceFingerprint")}</span>
          <bdi
            dir="ltr"
            className="chams-figure text-xs font-semibold leading-[1.5] text-muted-foreground"
          >
            {fingerprint}
          </bdi>
        </div>

        {state === "waiting" && (
          <Badge variant="warning" dot className="w-full justify-start px-[14px]">
            {t("entry.requestSent")}
          </Badge>
        )}
        {state === "review" && (
          <p className="flex items-center justify-between gap-3 rounded-[9px] border border-sunken bg-surface-2 px-[14px] py-3 text-[11.5px] text-muted-foreground">
            {t("entry.requester")}
            <span className="font-heading font-bold text-foreground">
              {locale === "ar" ? user.displayNameAr : user.displayNameFr}
            </span>
          </p>
        )}
        {state === "error" && (
          <p className="rounded-[9px] border border-destructive-border bg-destructive-soft px-[14px] py-3 text-[11.5px] leading-[1.6] text-destructive">
            {t("entry.dev4Detail")}
          </p>
        )}
      </Block>

      <Block className="!mb-8 flex flex-col gap-3">
        {state === "new" && (
          <Button
            size="lg"
            className="w-full rounded-[10px]"
            disabled={gate.busy}
            onClick={() => void gate.register()}
          >
            {t("entry.registerDevice")}
          </Button>
        )}
        {state === "waiting" && (
          <Button
            size="lg"
            variant="outline"
            className="w-full rounded-[10px]"
            disabled={gate.checking}
            onClick={() => void gate.checkAgain()}
          >
            {t("entry.checkAgain")}
          </Button>
        )}
        {state === "review" && (
          <>
            <Button
              size="lg"
              className="w-full rounded-[10px]"
              disabled={gate.busy}
              onClick={() => void gate.authorize()}
            >
              {t("entry.approve")}
            </Button>
            <Button
              size="lg"
              variant="destructive"
              className="w-full rounded-[10px]"
              disabled={gate.busy}
              onClick={onLogout}
            >
              {t("entry.reject")}
            </Button>
          </>
        )}
        {state === "error" && (
          <Button
            size="lg"
            variant="outline"
            className="w-full rounded-[10px]"
            disabled={gate.busy}
            onClick={() => void (gate.installation ? gate.authorize() : gate.register())}
          >
            {t("entry.retry")}
          </Button>
        )}
      </Block>
    </Page>
  );
}

/* ------------------------------------------------------------------ */
/* Offline setup                                                       */
/* ------------------------------------------------------------------ */

function MobilePinCells({
  id,
  label,
  value,
  onChange,
  showLabel,
  hideLabel,
  autoFocus = false,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly showLabel: string;
  readonly hideLabel: string;
  readonly autoFocus?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="flex flex-col gap-[9px]">
      <Label
        htmlFor={id}
        className="text-start font-heading text-[11.5px] font-bold text-muted-foreground"
      >
        {label}
      </Label>
      <div className="mobile-pin-cells flex items-stretch gap-2">
        <div className="relative min-w-0 flex-1">
          <input
            id={id}
            dir="ltr"
            type={revealed ? "text" : "password"}
            inputMode="numeric"
            pattern="[0-9]{8}"
            maxLength={PIN_LENGTH}
            autoComplete="new-password"
            autoFocus={autoFocus}
            required
            value={value}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(event) =>
              onChange(event.target.value.replaceAll(/\D/g, "").slice(0, PIN_LENGTH))
            }
            className="absolute inset-0 z-10 size-full cursor-text opacity-0"
          />
          <div dir="ltr" aria-hidden="true" className="flex gap-1.5">
            {Array.from({ length: PIN_LENGTH }, (_, index) => index).map((index) => {
              const filled = index < value.length;
              const active = focused && index === Math.min(value.length, PIN_LENGTH - 1);
              return (
                <span
                  key={index}
                  className={`grid h-[52px] flex-1 place-items-center rounded-[9px] border font-heading text-lg font-bold ${
                    active
                      ? "border-primary bg-card outline outline-2 outline-offset-1 outline-teal-200"
                      : "border-input bg-surface-2"
                  }`}
                >
                  {filled ? (
                    revealed ? (
                      value[index]
                    ) : (
                      "•"
                    )
                  ) : active ? (
                    <span className="h-5 w-0.5 bg-primary" />
                  ) : null}
                </span>
              );
            })}
          </div>
        </div>
        <button
          type="button"
          aria-label={revealed ? hideLabel : showLabel}
          aria-pressed={revealed}
          onClick={() => setRevealed((current) => !current)}
          className="chams-focusable grid size-[52px] shrink-0 place-items-center self-end rounded-[9px] border border-input bg-white text-muted-foreground transition-colors active:bg-surface-2"
        >
          {revealed ? (
            <EyeOff aria-hidden="true" className="size-[18px]" />
          ) : (
            <Eye aria-hidden="true" className="size-[18px]" />
          )}
        </button>
      </div>
    </div>
  );
}

export function MobileOfflineSetupScreen({
  locale,
  onLocaleChange,
  passkey,
  onReady,
}: {
  readonly locale: Locale;
  readonly onLocaleChange: () => void;
  readonly passkey: PasskeyUnlockMaterial | null;
  readonly onReady: (replica: StudentReplicaContract) => void;
}) {
  const t = (key: TranslationKey) => translate(locale, key);
  const setup = useOfflineSetup(onReady, passkey);
  const strong = setup.pin.length === PIN_LENGTH && !looksPredictable(setup.pin);

  return (
    <Page className="chams-page h-dvh overflow-y-auto bg-canvas" data-testid="offline-setup">
      <Navbar
        className="sticky top-0 z-30"
        title={t("entry.pinSetupTitle")}
        right={<LanguageButton locale={locale} onChange={onLocaleChange} />}
      />
      <form
        className="gate-form mx-auto w-full max-w-lg"
        onSubmit={(event: FormEvent) => stop(event, () => void setup.submit())}
      >
        <Block className="flex animate-fade-up flex-col gap-4">
          <div className="flex items-center gap-3">
            <span className="grid size-[38px] place-items-center rounded-[10px] bg-teal-50">
              <span aria-hidden="true" className="chams-logo size-5 bg-primary" />
            </span>
            <span className="flex flex-col gap-[3px]">
              <h1 className="font-heading text-base font-bold leading-[1.3]">
                {t("entry.pinSetupTitle")}
              </h1>
              <span className="text-[11.5px] leading-[1.5] text-muted-2">
                {t("entry.pinSetupSub")}
              </span>
            </span>
          </div>
          <p className="text-[12.5px] leading-[1.8] text-muted-foreground">
            {t("entry.pinSetupBody")}
          </p>

          <MobilePinCells
            id="mobile-offline-pin"
            label={t("entry.pinNew")}
            value={setup.pin}
            onChange={setup.setPin}
            showLabel={t("entry.pinShow")}
            hideLabel={t("entry.pinHide")}
            autoFocus
          />
          <MobilePinCells
            id="mobile-offline-pin-confirm"
            label={t("entry.pinConfirm")}
            value={setup.confirm}
            onChange={setup.setConfirm}
            showLabel={t("entry.pinShow")}
            hideLabel={t("entry.pinHide")}
          />

          <div className="flex flex-col gap-[7px] rounded-[10px] border border-sunken bg-surface-2 px-[15px] py-[13px]">
            <span
              className={`flex items-center gap-2 text-[11.5px] leading-[1.5] ${
                setup.pin.length === PIN_LENGTH ? "text-success" : "text-muted-2"
              }`}
            >
              <span
                aria-hidden="true"
                className={`grid size-[15px] place-items-center rounded-full font-heading text-[9px] font-extrabold ${
                  setup.pin.length === PIN_LENGTH
                    ? "bg-success text-white"
                    : "border-[1.5px] border-input"
                }`}
              >
                {setup.pin.length === PIN_LENGTH ? "✓" : ""}
              </span>
              {t("entry.pinRule1")}
            </span>
            <span
              className={`flex items-center gap-2 text-[11.5px] leading-[1.5] ${
                strong ? "text-success" : "text-muted-2"
              }`}
            >
              <span
                aria-hidden="true"
                className={`grid size-[15px] place-items-center rounded-full font-heading text-[9px] font-extrabold ${
                  strong ? "bg-success text-white" : "border-[1.5px] border-input"
                }`}
              >
                {strong ? "✓" : ""}
              </span>
              {t("entry.pinRule2")}
            </span>
          </div>

          {setup.mismatch && (
            <div className="flex animate-shake flex-col gap-1 rounded-[11px] border border-destructive-border bg-destructive-soft px-4 py-3.5 text-destructive">
              <span className="font-heading text-[12.5px] font-bold leading-[1.4]">
                {t("entry.pinMismatch")}
              </span>
              <span className="text-[11px] leading-[1.6]">{t("entry.pinMismatchBody")}</span>
            </div>
          )}
          {setup.busy && (
            <div className="flex flex-col gap-[7px] rounded-[11px] border border-teal-100 bg-teal-50 px-4 py-3.5">
              <span className="font-heading text-[12.5px] font-bold leading-[1.4] text-primary-strong">
                {t("entry.pinPreparing")}
              </span>
              <span className="h-[5px] overflow-hidden rounded-[3px] bg-teal-100">
                <span className="block h-full w-2/5 rounded-[3px] bg-primary motion-safe:animate-pulse" />
              </span>
            </div>
          )}
        </Block>

        <Block className="!mb-8">
          <Button type="submit" size="lg" disabled={setup.busy} className="w-full rounded-[10px]">
            {t("entry.pinSave")}
          </Button>
        </Block>
      </form>
    </Page>
  );
}

/* ------------------------------------------------------------------ */
/* Locked                                                              */
/* ------------------------------------------------------------------ */

export function MobileLockedScreen({
  locale,
  onlineReauthorized,
  pendingPasskey,
  onLocaleChange,
  onUnlocked,
  onOnlineSignIn,
}: {
  readonly locale: Locale;
  readonly onlineReauthorized: boolean;
  readonly pendingPasskey: PasskeyUnlockMaterial | null;
  readonly onLocaleChange: () => void;
  readonly onUnlocked: (replica: StudentReplicaContract) => void | Promise<void>;
  readonly onOnlineSignIn: () => void;
}) {
  const t = (key: TranslationKey) => translate(locale, key);
  const unlock = useUnlock(locale, onlineReauthorized, onUnlocked, pendingPasskey);
  const keypad = useLockedScreen(unlock);

  if (
    unlock.errorCode === "ONLINE_REAUTHORIZATION_REQUIRED" ||
    unlock.errorCode === "CORRUPT_CACHE"
  )
    return (
      <Page className="chams-page bg-canvas" data-testid="locked-gate">
        <Navbar
          className="sticky top-0 z-30"
          title={t("entry.lockedTitle")}
          right={<LanguageButton locale={locale} onChange={onLocaleChange} />}
        />
        <Block className="flex justify-center">
          {unlock.errorCode === "ONLINE_REAUTHORIZATION_REQUIRED" ? (
            <LockedTerminalCard
              tone="warning"
              icon={<span aria-hidden="true" className="chams-logo size-5 bg-warning" />}
              title={t("entry.lockoutTitle")}
              body={t("entry.lockoutBody")}
            >
              <Button size="lg" className="w-full rounded-[10px]" onClick={onOnlineSignIn}>
                {t("entry.signInOnline")}
              </Button>
            </LockedTerminalCard>
          ) : (
            <LockedTerminalCard
              tone="destructive"
              icon={<span aria-hidden="true" className="chams-logo size-5 bg-destructive" />}
              title={t("entry.corruptTitle")}
              body={t("entry.corruptBody")}
              detail="CORRUPT_CACHE"
            >
              <div className="flex flex-col gap-3">
                <Button size="lg" className="w-full rounded-[10px]" onClick={onOnlineSignIn}>
                  {t("entry.rebuildCopy")}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full rounded-[10px]"
                  onClick={onOnlineSignIn}
                >
                  {t("entry.signInOnline")}
                </Button>
              </div>
            </LockedTerminalCard>
          )}
        </Block>
      </Page>
    );

  return (
    <Page
      colors={{ bgIos: "bg-primary-strong", bgMaterial: "bg-primary-strong" }}
      className="chams-page mobile-locked-page relative flex min-h-screen flex-col overflow-y-auto"
      data-testid="locked-gate"
    >
      <span
        aria-hidden="true"
        className="chams-logo pointer-events-none absolute -top-[100px] -start-[120px] size-[400px] bg-white opacity-[0.08]"
      />
      <div className="relative flex justify-end px-2 pt-2">
        <LanguageButton locale={locale} onChange={onLocaleChange} tone="dark" />
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col items-center px-6 pt-[clamp(0.5rem,3dvh,2rem)]">
        <span
          aria-hidden="true"
          className="chams-logo size-[clamp(2.25rem,7dvh,2.875rem)] bg-white"
        />
        <h1 className="mt-[clamp(0.75rem,2.5dvh,1.25rem)] font-heading text-[19px] font-bold leading-[1.3] text-white">
          {t("entry.lockedTitle")}
        </h1>
        <p className="mt-2 max-w-[30ch] text-center text-[12.5px] leading-[1.6] text-white/60">
          {keypad.wrong ? t("entry.lockSubWrong") : t("entry.lockSubIdle")}
        </p>
        <div className="relative mt-[clamp(1rem,3dvh,1.75rem)] w-full">
          <input
            dir="ltr"
            type="text"
            inputMode="none"
            pattern="[0-9]{8}"
            maxLength={PIN_LENGTH}
            autoComplete="off"
            aria-label={t("entry.lockedTitle")}
            value={unlock.pin}
            onChange={(event) =>
              unlock.setPin(event.target.value.replaceAll(/\D/g, "").slice(0, PIN_LENGTH))
            }
            className="absolute inset-0 z-10 size-full rounded-md bg-transparent text-transparent caret-transparent outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          />
          <div
            key={keypad.attempt}
            dir="ltr"
            className={`flex h-5 items-center justify-center gap-3 ${
              keypad.wrong ? "animate-shake" : ""
            }`}
          >
            <PinDots length={unlock.pin.length} tone="dark" />
          </div>
        </div>
        <p
          role="status"
          className={`mt-4 font-heading text-xs font-bold text-[#ffc9c5] transition-opacity duration-base ${
            keypad.wrong ? "opacity-100" : "opacity-0"
          }`}
        >
          {unlock.error ?? t("entry.wrongPin")}
        </p>
      </div>

      <div className="relative mt-auto shrink-0 px-[22px] pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
        <div className="mobile-lock-keypad">
          <Keypad
            tone="dark"
            disabled={unlock.busy || unlock.passkeyBusy}
            onDigit={keypad.press}
            onBackspace={keypad.backspace}
            backspaceLabel={t("sp.back")}
            passkeyBusy={unlock.passkeyBusy}
            onPasskey={() => void unlock.submitPasskey()}
            passkeyLabel={t("entry.usePasskey")}
          />
        </div>
        <button
          type="button"
          onClick={onOnlineSignIn}
          className="mt-[clamp(0.75rem,2dvh,1.125rem)] min-h-[46px] w-full rounded-[14px] border border-white/25 font-heading text-[13px] font-bold text-white outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          {t("entry.useAnotherAccount")}
        </button>
      </div>
    </Page>
  );
}
